import * as React from "react"
import { Cross2Icon } from "@radix-ui/react-icons"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@src/lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "sl-fixed sl-top-0 sl-z-[100] sl-flex sl-max-h-screen sl-w-full sl-flex-col-reverse sl-p-4 sm:sl-bottom-0 sm:sl-right-0 sm:sl-top-auto sm:sl-flex-col md:sl-max-w-[420px]",
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "sl-group sl-pointer-events-auto sl-relative sl-flex sl-w-full sl-items-center sl-justify-between sl-space-x-2 sl-overflow-hidden sl-rounded-md sl-border sl-p-4 sl-pr-6 sl-shadow-lg sl-transition-all data-[swipe=cancel]:sl-translate-x-0 data-[swipe=end]:sl-translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:sl-translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:sl-transition-none data-[state=open]:sl-animate-in data-[state=closed]:sl-animate-out data-[swipe=end]:sl-animate-out data-[state=closed]:sl-fade-out-80 data-[state=closed]:sl-slide-out-to-right-full data-[state=open]:sl-slide-in-from-top-full data-[state=open]:sm:sl-slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "sl-border sl-bg-background sl-text-foreground",
        destructive:
          "sl-destructive sl-group sl-border-destructive sl-bg-destructive sl-text-destructive-foreground",
        success:
          "sl-success sl-group sl-border-green-500 sl-bg-green-50 sl-text-green-500"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
)

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "sl-inline-flex sl-h-8 sl-shrink-0 sl-items-center sl-justify-center sl-rounded-md sl-border sl-bg-transparent sl-px-3 sl-text-sm sl-font-medium sl-transition-colors hover:sl-bg-secondary focus:sl-outline-none focus:sl-ring-1 focus:sl-ring-ring disabled:sl-pointer-events-none disabled:sl-opacity-50 group-[.sl-destructive]:sl-border-muted/40 group-[.sl-destructive]:hover:sl-border-destructive/30 group-[.sl-destructive]:hover:sl-bg-destructive group-[.sl-destructive]:hover:sl-text-destructive-foreground group-[.sl-destructive]:focus:sl-ring-destructive group-[.sl-success]:sl-border-muted/40 group-[.sl-success]:hover:sl-border-green-50/30 group-[.sl-success]:hover:sl-bg-green-50 group-[.sl-success]:hover:sl-text-green-500 group-[.sl-success]:focus:sl-ring-green-500",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "sl-absolute sl-right-1 sl-top-1 sl-rounded-md sl-p-1 sl-text-foreground/50 sl-opacity-0 sl-transition-opacity hover:sl-text-foreground focus:sl-opacity-100 focus:sl-outline-none focus:sl-ring-1 group-hover:sl-opacity-100 group-[.sl-destructive]:sl-text-red-300 group-[.sl-destructive]:hover:sl-text-red-50 group-[.sl-destructive]:focus:sl-ring-red-400 group-[.sl-destructive]:focus:sl-ring-offset-red-600 group-[.sl-success]:sl-text-red-300 group-[.sl-success]:hover:sl-text-red-50 group-[.sl-success]:focus:sl-ring-red-400 group-[.sl-success]:focus:sl-ring-offset-red-600",
      className
    )}
    toast-close=""
    {...props}
  >
    <Cross2Icon className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("sl-text-sm sl-font-semibold [&+div]:sl-text-xs", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("sl-text-sm sl-opacity-90", className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction
}
